// DASHBOARD LAYOUT — MODERN + ROLE BASED VERSION

import { ReactNode, useState, useEffect } from "react";
import { AppSidebar } from "./AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { SubscriptionGate } from "./SubscriptionGate";

import {
  Bell,
  User,
  LogOut,
  Building2,
  Shield,
  Key,
  Database,
  Flag,
  Users,
  DollarSign,
  Maximize,
  Calculator,
  X,
  ArrowLeftRight,
  Settings
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/hooks/useAuth";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface DashboardLayoutProps {
  children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const navigate = useNavigate();
  const { role, isAdmin, isSuperAdmin } = useUserRole();
  const { user, organizationName, signOut } = useAuth();

  const [showCurrency, setShowCurrency] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);

  const isDevUser =
    user && (user.email === "dev@developer.local" || user.username === "dev");

  const toggleFullScreen = () => {
    if (!document.fullscreenElement)
      document.documentElement.requestFullscreen();
    else document.exitFullscreen();
  };

  const handleLogout = async () => {
    await signOut();
  };

  return (
    <SubscriptionGate>
      <SidebarProvider defaultOpen>
        <div className="min-h-screen flex w-full bg-white">
          <AppSidebar />

          <div className="flex-1 flex flex-col">

            {/* HEADER */}
            <header className="h-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white flex items-center justify-between px-6 shadow-md">

              {/* LEFT: PUMP & USER INFO */}
              <div className="flex items-center gap-6">
                <div className="flex flex-col">
                  <h1 className="text-lg font-black tracking-tight leading-none uppercase">
                    {isDevUser ? "🔧 Developer Mode" : organizationName || "Loading..."}
                  </h1>
                  {!isDevUser && user?.tenant?.superAdminEmail && (
                    <span className="text-[10px] font-bold text-blue-200 uppercase tracking-widest mt-1">
                      Verified: {user.tenant.superAdminEmail}
                    </span>
                  )}
                </div>

                <div className="h-8 w-px bg-white/10 hidden md:block" />

                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5 text-blue-300" />
                    <span className="text-sm font-bold tracking-wide">
                      {user?.username || user?.fullName || user?.email?.split('@')[0] || "User"}
                    </span>
                  </div>
                  {!isDevUser && user?.email && (
                    <span className="text-[9px] opacity-60 ml-5">
                      {user.email}
                    </span>
                  )}
                </div>
              </div>

              {/* RIGHT */}
              <div className="flex items-center gap-3">

                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-white/20"
                  onClick={() => setShowCurrency(true)}
                >
                  <DollarSign />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-white/20"
                  onClick={toggleFullScreen}
                >
                  <Maximize />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-white/20"
                  onClick={() => setShowCalculator(true)}
                >
                  <Calculator />
                </Button>

                {/* ROLE PANEL */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="bg-white/10 hover:bg-white/20 text-white font-bold tracking-widest">
                      {isSuperAdmin ? 'ADMIN' : (role === 'manager' ? 'MANAGER' : 'OPERATOR')}
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end" className="w-64 rounded-2xl shadow-2xl border-blue-50">

                    <DropdownMenuLabel className="text-blue-900 font-extrabold px-4 py-3 text-sm tracking-tight uppercase opacity-50">
                      Control Panel
                    </DropdownMenuLabel>

                    <DropdownMenuSeparator className="bg-slate-100" />

                    {/* ADMIN ONLY AREA */}
                    {isSuperAdmin && (
                      <div className="py-1">
                        <DropdownMenuItem onClick={() => navigate("/organization-settings")} className="px-4 py-2.5 focus:bg-blue-50 focus:text-blue-700 cursor-pointer">
                          <Building2 className="mr-3 h-4 w-4 text-blue-600" />
                          <span className="font-semibold text-[13px]">Organization Details</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem onClick={() => navigate("/role-permissions")} className="px-4 py-2.5 focus:bg-blue-50 focus:text-blue-700 cursor-pointer">
                          <Shield className="mr-3 h-4 w-4 text-blue-600" />
                          <span className="font-semibold text-[13px]">Role Permission</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem onClick={() => navigate("/system-settings")} className="px-4 py-2.5 focus:bg-blue-50 focus:text-blue-700 cursor-pointer">
                          <Settings className="mr-3 h-4 w-4 text-blue-600" />
                          <span className="font-semibold text-[13px]">System Settings</span>
                        </DropdownMenuItem>
                      </div>
                    )}

                    {isSuperAdmin && <DropdownMenuSeparator className="bg-slate-100" />}

                    {/* ADMIN & MANAGER AREA */}
                    {(isAdmin || isSuperAdmin) && (
                      <div className="py-1">
                        <DropdownMenuItem onClick={() => navigate("/backup-data")} className="px-4 py-2.5 focus:bg-blue-50 focus:text-blue-700 cursor-pointer">
                          <Database className="mr-3 h-4 w-4 text-blue-600 opacity-70" />
                          <span className="font-semibold text-[13px]">Backup Data</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem onClick={() => navigate("/my-bunks")} className="px-4 py-2.5 focus:bg-blue-50 focus:text-blue-700 cursor-pointer">
                          <Users className="mr-3 h-4 w-4 text-blue-600 opacity-70" />
                          <span className="font-semibold text-[13px]">Bunk Users</span>
                        </DropdownMenuItem>
                      </div>
                    )}

                    {(isAdmin || isSuperAdmin) && <DropdownMenuSeparator className="bg-slate-100" />}

                    {/* EVERYONE AREA */}
                    <div className="py-1">
                      <DropdownMenuItem onClick={() => navigate("/change-password")} className="px-4 py-2.5 focus:bg-blue-50 focus:text-blue-700 cursor-pointer">
                        <Key className="mr-3 h-4 w-4 text-slate-500 opacity-70" />
                        <span className="font-semibold text-[13px]">Change Password</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem onClick={() => navigate("/feedback")} className="px-4 py-2.5 focus:bg-blue-50 focus:text-blue-700 cursor-pointer">
                        <Flag className="mr-3 h-4 w-4 text-slate-500 opacity-70" />
                        <span className="font-semibold text-[13px]">Report Us</span>
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button variant="ghost" size="icon">
                  <Bell />
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <User />
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

              </div>
            </header>

            <main className="flex-1 p-6">{children}</main>
          </div>
        </div>

        {showCurrency && <CurrencyModal close={() => setShowCurrency(false)} />}
        {showCalculator && <CalculatorModal close={() => setShowCalculator(false)} />}

      </SidebarProvider>
    </SubscriptionGate>
  );
}

/* ===============================
   Currency Converter
================================ */

function CurrencyModal({ close }: any) {

  const [amount, setAmount] = useState(1);
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("INR");
  const [result, setResult] = useState<number | null>(null);

  useEffect(() => {
    convert();
  }, [amount, from, to]);

  const convert = async () => {
    const res = await fetch(`https://open.er-api.com/v6/latest/${from}`);
    const data = await res.json();
    const rate = data.rates[to];
    setResult(rate * amount);
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[720px] max-h-[90vh] overflow-auto shadow-2xl p-6">
        <div className="flex justify-between mb-4">
          <h2 className="font-semibold text-lg text-blue-900">Currency Converter</h2>
          <X onClick={close} className="cursor-pointer"/>
        </div>
        <div className="flex gap-3 items-center">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            className="border p-2 rounded w-28"
          />
          <select value={from} onChange={(e) => setFrom(e.target.value)} className="border p-2 rounded">
            {currencyList.map(c => (
              <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
            ))}
          </select>
          <Button variant="outline" onClick={() => { setFrom(to); setTo(from); }}>
            <ArrowLeftRight/>
          </Button>
          <select value={to} onChange={(e) => setTo(e.target.value)} className="border p-2 rounded">
            {currencyList.map(c => (
              <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
            ))}
          </select>
        </div>
        {result && (
          <div className="mt-6 text-xl font-semibold">
            {amount} {from} = {result.toFixed(2)} {to}
          </div>
        )}
      </div>
    </div>
  );
}

/* ===============================
   Calculator
================================ */

function CalculatorModal({ close }: any) {
  const [input, setInput] = useState("");

  const calculate = () => {
    try {
      setInput(eval(input).toString());
    } catch {
      setInput("Error");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[320px] shadow-2xl p-4">
        <div className="flex justify-between mb-3">
          <h2 className="font-semibold text-blue-900">Calculator</h2>
          <X onClick={close} className="cursor-pointer"/>
        </div>
        <input
          value={input}
          readOnly
          className="border w-full p-2 mb-3 text-right rounded text-lg"
        />
        <div className="grid grid-cols-4 gap-2">
          {["7","8","9","/","4","5","6","*","1","2","3","-","0",".","=","+"].map(btn => (
            <Button
              key={btn}
              variant={btn === "=" ? "default" : "outline"}
              onClick={() => {
                if (btn === "=") calculate();
                else setInput(prev => prev + btn);
              }}
            >
              {btn}
            </Button>
          ))}
          <Button variant="destructive" onClick={() => setInput("")}>C</Button>
        </div>
      </div>
    </div>
  );
}

/* ===============================
   Currency List
================================ */

const currencyList = [
  { code: "USD", flag: "🇺🇸" },
  { code: "EUR", flag: "🇪🇺" },
  { code: "INR", flag: "🇮🇳" },
  { code: "GBP", flag: "🇬🇧" },
  { code: "JPY", flag: "🇯🇵" },
  { code: "AUD", flag: "🇦🇺" },
  { code: "CAD", flag: "🇨🇦" },
  { code: "CNY", flag: "🇨🇳" },
  { code: "CHF", flag: "🇨🇭" },
  { code: "SGD", flag: "🇸🇬" },
];
